import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { DropdownWithAccordion } from "../components/ui/dropdown";
import { SelectedFilterList } from "../components/features/SelectedFilterList";
import { useParams } from "react-router-dom";
import {
  useGetAccountsFiltersQuery,
  useGetAccountsPlanListQuery,
  useGetCampaignSummaryQuery,
} from "../api";
import { Order } from "../types";
import { useEffect, useMemo, useState } from "react";
import { ResetAllFiltersButton } from "../components/features/ResetAllFiltersButton";
import { prepareFilters } from "../utils/prepareFilters";
import { InputWithQuery } from "../components/ui/InputWithQuery";
import { AccountPlanStatus } from "../types/enums";
import {
  useAppSelector,
  useHandleError,
  useStateWithSearchParams,
} from "../hooks";
import {
  ExportAccountsPlanPDFBtn,
  TargetAccountPlansBtn,
  ToTopBtn,
  UpdateAccountsPlanBtn,
} from "../components/ui/buttons";
import { QUERY_SEPARATOR } from "../constant";
import { CustomCalendar } from "../components/ui/calendar";
import {
  AccountPlanCard,
  InfiniteScroll,
  NoContentBlock,
  TableTabs,
} from "../components/ui";
import { DropdownWithQuery } from "../components/ui/dropdown/DropdownWithQuery";
import { AccountPlanItem, WithId } from "../types/apiTypes";

type SearchParams = {
  sizes?: string;
  industries?: string;
  regions?: string;
  search?: string;
  sort?: string;
  selected?: string;
  date?: string;
  status_filter?: string;
};

enum ColumnType {
  grid = "grid",
  row = "row",
}
const statusFilterList = ["All", "Ready", "Needs Update", "Missing"];

const statusFilterMapper: Record<string, string | undefined> = {
  All: undefined,
  Ready: AccountPlanStatus.completed,
  "Needs Update": AccountPlanStatus.needs_update,
  Missing: AccountPlanStatus.ready,
};

const AccountsReportsPage = () => {
  const { campaign_id } = useParams();
  const { isEditor } = useAppSelector((state) => state.company.userRoles);

  const [page, setPage] = useState<number>(0);
  const [columnsType, setColumnsType] = useState<ColumnType>(ColumnType.grid);

  const { searchDict, setSearchValue } = useStateWithSearchParams<SearchParams>(
    {
      industries: undefined,
      regions: undefined,
      sizes: undefined,
      search: undefined,
      sort: undefined,
      selected: undefined,
      date: undefined,
      status_filter: undefined,
    },
  );

  const filters = useGetAccountsFiltersQuery(
    {
      campaign_id: campaign_id!,
    },
    { skip: !campaign_id, refetchOnMountOrArgChange: true },
  );

  const { data, isFetching, isLoading, error } = useGetAccountsPlanListQuery(
    {
      campaign_id: campaign_id!,
      size: searchDict?.sizes?.split(QUERY_SEPARATOR),
      search: searchDict.search || undefined,
      industry: searchDict?.industries?.split(QUERY_SEPARATOR),
      region: searchDict?.regions?.split(QUERY_SEPARATOR),
      page: page,
      per_page: 10,
      sort: searchDict?.sort?.split("-")[0],
      order: searchDict?.sort?.split("-")[1] as Order,
      status_filter: isEditor
        ? (statusFilterMapper[
            searchDict?.status_filter || "All"
          ] as AccountPlanStatus)
        : AccountPlanStatus.completed,
      start_date: searchDict.date?.split(QUERY_SEPARATOR)[0],
      end_date: searchDict.date?.split(QUERY_SEPARATOR)[1],
    },
    { skip: !campaign_id, refetchOnMountOrArgChange: true },
  );

  useHandleError(error);

  const { data: summary } = useGetCampaignSummaryQuery({
    campaign_id: campaign_id!,
  });

  useEffect(() => {
    setPage(0);
  }, [searchDict?.status_filter]);

  useEffect(() => {
    if (
      searchDict.search ||
      searchDict.sort ||
      searchDict.sizes ||
      searchDict.industries ||
      searchDict.regions
    )
      setPage(0);
  }, [
    searchDict.search,
    searchDict.sort,
    searchDict.sizes,
    searchDict.industries,
    searchDict.regions,
  ]);

  const removeFilterHandler = (key: string, filter: string) => {
    const filters = searchDict[key as keyof SearchParams];
    if (!filters) return;

    setSearchValue(
      key as keyof SearchParams,
      filters
        .split(QUERY_SEPARATOR)
        .filter((el) => el !== filter)
        .join(QUERY_SEPARATOR),
    );
  };

  const isFilters =
    prepareFilters(filters.data).some(
      ({ query }) => searchDict[query as keyof SearchParams],
    ) ||
    searchDict.date ||
    searchDict?.status_filter;

  const isGrid = ColumnType.grid === columnsType;

  const canLoadMore = page + 1 < (data?.total_pages || 0);

  const transformData = useMemo(
    () =>
      (data?.items || []).map((el) => ({
        ...el,
        id: el.account_id,
      })),
    [data?.items],
  );

  return (
    <Stack gap="12px">
      <Typography component={"h3"} variant={"subtitle1"} fontWeight={500}>
        Account Plans
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <TableTabs isGrid={isGrid} setColumnsType={setColumnsType} />
        <InputWithQuery />
        {isEditor && (
          <Stack>
            <DropdownWithQuery
              query={"status_filter"}
              label={""}
              items={statusFilterList}
              placeholder={""}
              initSelected={statusFilterList[0]}
              maxHeight="134px"
              maxItemsWidth={"172px"}
            />
          </Stack>
        )}

        <Stack flexDirection={"row"} gap="12px">
          {prepareFilters(filters.data).length > 0 && (
            <DropdownWithAccordion
              isActive={prepareFilters(filters.data).some(
                ({ query }) => searchDict[query as keyof SearchParams],
              )}
              data={prepareFilters(filters.data)}
              selected={prepareFilters(filters.data).reduce(
                (acc, { query }) =>
                  acc +
                  (searchDict[query as keyof SearchParams]?.split(
                    QUERY_SEPARATOR,
                  )?.length || 0),
                0,
              )}
            />
          )}
          <Divider orientation="vertical" flexItem sx={{ flexShrink: 1 }} />
          <CustomCalendar date={searchDict.date} height={"42px"} />
          {isFilters && <ResetAllFiltersButton />}
        </Stack>

        {summary && (
          <Stack
            direction="row"
            gap="12px"
            marginLeft="auto"
            alignItems="center"
          >
            {summary.one_account_plan_complete && (
              <ExportAccountsPlanPDFBtn
                campaign_id={campaign_id!}
                hideButton={false}
              />
            )}
            {isEditor && (
              <>
                {summary.has_reports_needing_update && (
                  <UpdateAccountsPlanBtn
                    campaign_id={campaign_id!}
                    hideButton={false}
                  />
                )}

                {summary.has_accounts_without_plan && (
                  <TargetAccountPlansBtn
                    isActionBlock={true}
                    campaign_id={campaign_id!}
                    isGeneratingAllAccountPlan={
                      summary.account_plan_generating_all === "in_progress"
                    }
                  />
                )}
              </>
            )}
          </Stack>
        )}
      </Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        gap="16px"
        alignItems="flex-end"
      >
        <Stack direction="row" flexWrap="wrap" gap="8px">
          {prepareFilters(filters.data).map(({ placeholder, query }) => (
            <SelectedFilterList
              key={query}
              filters={
                searchDict[query as keyof SearchParams]?.split(
                  QUERY_SEPARATOR,
                ) || []
              }
              label={placeholder}
              removeHandler={removeFilterHandler}
            />
          ))}
        </Stack>
      </Stack>
      <InfiniteScroll<AccountPlanItem & WithId>
        hasMore={canLoadMore}
        isLoading={isFetching}
        loadMore={() => setPage((prev) => prev + 1)}
        page={page}
        items={transformData}
        scrollOffset={1200}
        showScrollTopOffset={100}
        startPage={0}
      >
        {({ showScrollTop, items }) => (
          <>
            {isLoading ? (
              <Box
                display="flex"
                height="25vh"
                justifyContent="center"
                alignItems="center"
              >
                <CircularProgress size={80} sx={{ color: "#FE8836" }} />
              </Box>
            ) : (
              <>
                {items.length === 0 &&
                !isFetching &&
                (isFilters || !!searchDict.search) ? (
                  <Stack
                    gap="8px"
                    alignItems="center"
                    justifyContent="center"
                    padding="32px"
                  >
                    <NoContentBlock
                      title={"No Matching Account Plans"}
                      text={
                        "No account plans match your search. " +
                        "Please try another keyword."
                      }
                    />
                  </Stack>
                ) : (
                  <Stack
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: `repeat(${isGrid ? 2 : 1}, 1fr)`,
                        md: `repeat(${isGrid ? 3 : 1}, 1fr)`,
                        lg: `repeat(${isGrid ? 4 : 1}, 1fr)`,
                      },
                      gap: "12px",
                    }}
                  >
                    {items.map(
                      ({
                        account_id,
                        account_name,
                        updated_at,
                        needs_update,
                        status,
                        totalContacts,
                        is_new,
                      }) => (
                        <AccountPlanCard
                          key={account_id}
                          id={account_id}
                          campaign_id={campaign_id!}
                          name={account_name}
                          updatedAt={updated_at}
                          isGrid={isGrid}
                          isNeedsUpdate={needs_update}
                          status={status}
                          totalContacts={totalContacts || 25}
                          isNew={is_new}
                          isEditor={isEditor}
                        />
                      ),
                    )}

                    {showScrollTop && <ToTopBtn />}
                  </Stack>
                )}
              </>
            )}
          </>
        )}
      </InfiniteScroll>
      {(canLoadMore || data?.total_pages === page) &&
        isFetching &&
        !isLoading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={"8vh"}
          >
            <CircularProgress size={30} sx={{ color: "#FE8836" }} />
          </Box>
        )}
    </Stack>
  );
};

export default AccountsReportsPage;
